import { InjectionToken, Signal } from '@angular/core';
import { Observable } from 'rxjs';

import {
  IChatProfile,
  IChatRoom,
  IFileUploadMetadata,
  Message,
  PublishMessageResponse
} from '@sondermind/utilities/models-chat';

import { ChatInitStatus } from './init/chat-init-status.enum';

/**
  * PEG-835: This is the initial attempt at a unified ChatStore interface.
  * It will be extended to accommodate necessary functionality.
  * Any methods that turn out to be unneeded should be removed.
  */
export interface IChatStore {
  /** Where chat initialization stands; `failed` is never reported as `ready`. */
  initStatus: Signal<ChatInitStatus>;

  initializeChat$: () => Observable<unknown>;
  teardownChat$: () => Observable<unknown>;

  acknowledgeChat: () => void;
  getActiveRoom$: () => Observable<IChatRoom>;
  getLoadedMessages$: () => Observable<Message[]>;
  getTotalUnreadCount$: () => Observable<number>;
  isPubnubOnline$: () => Observable<boolean>;
  loadMoreMessages$: () => Observable<void>;

  openChatRoomByPlatformIdentity$: (platformIdentity: string) => Observable<IChatRoom>;
  openChatRoomByRoomUuid$: (roomUuid: string) => Observable<IChatRoom>;
  getChatParticipantOptions$: () => Observable<IChatProfile[]>;
  getChatRooms$: () => Observable<IChatRoom[]>;
  /**
   * Publishes a message into a room, connected to any file attachments.
   * @param roomUuid The UUID of the room.
   * @param message The message to be published.
   * @param fileAttachmentUuids The UUIDs of the file attachments connected to the message.
   * @returns The publish response, including success flags and timetoken.
   */
  publishChatMessage$: (
    roomUuid: string,
    message: string,
    fileAttachmentUuids: string[]
  ) => Observable<PublishMessageResponse>;

  sendSingleMessageToChatRoom$: (
    platformIdentity: string,
    chatMessage: string
  ) => Observable<PublishMessageResponse>;

  /**
   * Uploads a file attachment to the back-end, which sends it to S3 and returns metadata
   * for us to attach to the PubNub message.
   * @param roomUuid - The UUID of the chat room.
   * @param file - The file to upload.
   * @returns An observable of the file upload metadata.
   */
  uploadFileAttachment$: (roomUuid: string, file: File) => Observable<IFileUploadMetadata>;

  /**
   * Downloads a file attachment from the back-end.
   * @param roomUuid - The UUID of the chat room.
   * @param fileUuid - The UUID of the file attachment.
   * @returns An observable of the file attachment.
   */
  downloadFileAttachment$: (roomUuid: string, fileUuid: string) => Observable<Blob>;

  deactivateChatRoom: () => void;
}

export const CHAT_STORE = new InjectionToken<IChatStore>('CHAT_STORE');
